import {
  Alert,
  AlertDescription,
  AlertIcon,
  AspectRatio,
  Badge,
  Box,
  Button as ChakraButton,
  CloseButton,
  HStack,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Select,
  SimpleGrid,
  Skeleton,
  SkeletonText,
  Text,
  Tooltip,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import {
  AdminLayout,
  Button,
  Card,
  SearchInput,
  SelectInput,
  StatsCard,
} from '@grcs/components';
import { SEO } from '@grcs/components/SEO';
import { useData } from '@grcs/context/DataContext';
import {
  useHomeworkOperations,
  useSemesterOperations,
} from '@grcs/hook/useDatabase';
import { useLocalStorage } from '@grcs/hook/useLocalStorage';
import book_icon from '@grcs/images/default_book_icon.png';
import {
  ArticleProfile,
  HomeworkStudentProfile,
  HomeworkV2Profile,
} from '@grcs/types/DataTypes';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  FiBookOpen,
  FiCalendar,
  FiChevronLeft,
  FiChevronRight,
  FiClock,
  FiFilter,
  FiGrid,
  FiInfo,
  FiList,
  FiPlay,
  FiTrendingUp,
} from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';

interface HomeworkWithArticle extends HomeworkV2Profile {
  article?: ArticleProfile & { imageSrc?: string };
  timeRemaining?: string;
  difficulty?: 'easy' | 'medium' | 'hard';
  estimatedTime?: number;
}

interface StudentStats {
  totalPending: number;
  completedThisWeek: number;
  averageScore: number;
  streakDays: number;
}

type ViewMode = 'grid' | 'list';
type SortBy = 'difficulty' | 'title' | 'created_at';
type MenuLanguage = 'en' | 'zh';

const menuText = {
  en: {
    menuLanguageLabel: 'Menu Language',
    menuLanguageEnglish: 'English',
    menuLanguageChinese: '中文',
    headerTitle: 'My Homework',
    pendingAssignments: (count: number) => `${count} pending assignments`,
    semesterLabel: (semester: string) => `Semester ${semester}`,
    gridView: 'Grid View',
    listView: 'List View',
    emptyTitle: 'No homework assignments yet',
    emptyDescription:
      'New assignments will appear here when they become available.',
    startHomework: 'Start Homework',
    showLabel: 'Show:',
    perPage: 'per page',
    showingRange: (from: number, to: number, total: number) =>
      `Showing ${from}-${to} of ${total} assignments`,
    noAssignmentsFound: 'No assignments found',
    homeworkPreviewTitle: (title: string) => `Homework Preview: ${title}`,
    gradeLabel: (grade: string) => `Grade ${grade}`,
    estimatedLabel: (minutes?: number) => `Estimated: ${minutes} minutes`,
    articlePreviewLabel: 'Article Preview:',
    noArticlePreview: 'No article content available for preview.',
    close: 'Close',
    startNow: 'Start Now',
    previousPage: 'Previous page',
    nextPage: 'Next page',
    timeMinutes: (minutes?: number) => `${minutes} min`,
  },
  zh: {
    menuLanguageLabel: '菜单语言',
    menuLanguageEnglish: 'English',
    menuLanguageChinese: '中文',
    headerTitle: '我的作业',
    pendingAssignments: (count: number) => `待完成作业 ${count} 项`,
    semesterLabel: (semester: string) => `学期 ${semester}`,
    gridView: '网格',
    listView: '列表',
    emptyTitle: '暂无作业',
    emptyDescription: '有新作业时会显示在这里。',
    startHomework: '开始',
    showLabel: '显示:',
    perPage: '每页',
    showingRange: (from: number, to: number, total: number) =>
      `显示第 ${from}-${to} 条，共 ${total} 条作业`,
    noAssignmentsFound: '未找到作业',
    homeworkPreviewTitle: (title: string) => `作业预览: ${title}`,
    gradeLabel: (grade: string) => `${grade} 年级`,
    estimatedLabel: (minutes?: number) => `预计: ${minutes} 分钟`,
    articlePreviewLabel: '文章预览:',
    noArticlePreview: '暂无可预览的文章内容。',
    close: '关闭',
    startNow: '立即开始',
    previousPage: '上一页',
    nextPage: '下一页',
    timeMinutes: (minutes?: number) => `${minutes} 分钟`,
  },
} as const;

export const StudentPendingEnhanced: React.FC = () => {
  const [user] = useLocalStorage('user', {});
  const homeworkOps = useHomeworkOperations();
  const semesterOps = useSemesterOperations();
  const { supabase } = useData();
  const navigate = useNavigate();
  const toast = useToast();
  const {
    isOpen: isPreviewModalOpen,
    onOpen: onPreviewModalOpen,
    onClose: onPreviewModalClose,
  } = useDisclosure();

  // Data state
  const [, setHomeworkStudent] = useState<HomeworkStudentProfile[]>([]);
  const [homeworks, setHomeworks] = useState<HomeworkV2Profile[]>([]);
  const [articles, setArticles] = useState<ArticleProfile[]>([]);
  const [currentSemester, setCurrentSemester] = useState<string>('');
  const [semesterError, setSemesterError] = useState<string>('');
  const [selectedHomework, setSelectedHomework] =
    useState<HomeworkWithArticle | null>(null);

  // Loading states
  const [loading, setLoading] = useState(true);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // UI state
  const [showNotification, setShowNotification] = useState(true);
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [sortBy, setSortBy] = useState<SortBy>('created_at');
  const [searchQuery, setSearchQuery] = useState('');
  const [difficultyFilter, setDifficultyFilter] = useState<string>('all');
  const [menuLanguage, setMenuLanguage] = useLocalStorage(
    'user_language',
    'zh',
  );
  const resolvedMenuLanguage: MenuLanguage =
    menuLanguage === 'zh' ? 'zh' : 'en';
  const t = menuText[resolvedMenuLanguage];

  // Stats
  const [studentStats, setStudentStats] = useState<StudentStats>({
    totalPending: 0,
    completedThisWeek: 0,
    averageScore: 0,
    streakDays: 0,
  });

  // Optimized single-query homework fetching
  const fetchHomeworkData = useCallback(async () => {
    if (!currentSemester || semesterError) {
      console.log(
        '⏭️ Skipping homework data loading - no active semester or error present',
      );
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const student_token = user.user_token;

      console.log('📊 Fetching student homework with optimized query...');
      const startTime = Date.now();

      // Calculate pagination parameters
      const from = (currentPage - 1) * pageSize;

      // Fetch homework for the student in current semester only
      const { data: allHomeworkData, error: homeworkError } = await supabase
        .from('homework_student_relation')
        .select(
          `
          *,
          homework:homework_token!inner (
            homework_token,
            title,
            grade,
            chapter,
            created_at,
            article_token,
            class_token,
            class:class_token!inner (
              semester_year
            )
          ),
          library:article_token (
            id,
            title,
            author,
            grade,
            created_at,
            article
          )
        `,
        )
        .eq('student_token', student_token)
        .eq('homework.class.semester_year', currentSemester)
        .order('created_at', { ascending: false });

      if (homeworkError) {
        console.error('Homework query error:', homeworkError);
        throw new Error('Error fetching homework data');
      }

      // Filter to get only pending (not completed) homework
      const pendingHomeworkData =
        allHomeworkData?.filter((item: any) => {
          if (!item.question_attempted) return true; // Not attempted = pending
          try {
            const attempted = JSON.parse(item.question_attempted);
            return Array.isArray(attempted)
              ? attempted.length === 0
              : Object.keys(attempted).length === 0;
          } catch {
            return true; // Invalid data = treat as pending
          }
        }) || [];

      // Apply pagination to filtered results
      const homeworkData = pendingHomeworkData.slice(from, from + pageSize);
      const totalHomeworkCount = pendingHomeworkData.length;

      setTotalCount(totalHomeworkCount || 0);

      const loadTime = Date.now() - startTime;
      console.log(`✅ Homework data loaded in ${loadTime}ms`);

      if (homeworkData && homeworkData.length > 0) {
        // Process the joined data
        const processedHomework = homeworkData.map((item: any) => ({
          ...item.homework,
          article: item.library,
          student_relation: {
            completed: item.completed,
            created_at: item.created_at,
            full_name: item.full_name,
          },
        }));

        setHomeworks(processedHomework as HomeworkV2Profile[]);
        setHomeworkStudent(homeworkData as HomeworkStudentProfile[]);

        // Extract articles data for compatibility
        const articlesData = homeworkData
          .map((item: any) => item.library)
          .filter(Boolean);
        setArticles(articlesData as ArticleProfile[]);

        // Calculate stats efficiently
        const totalPending = totalHomeworkCount || 0;

        // Get completed homework count for this week (current semester only)
        const weekAgo = new Date(
          Date.now() - 7 * 24 * 60 * 60 * 1000,
        ).toISOString();
        const { data: recentHomework, error: recentError } = await supabase
          .from('homework_student_relation')
          .select(
            `
            question_attempted,
            updated_at,
            homework:homework_token!inner (
              class_token,
              class:class_token!inner (
                semester_year
              )
            )
          `,
          )
          .eq('student_token', student_token)
          .eq('homework.class.semester_year', currentSemester)
          .gte('updated_at', weekAgo);

        if (recentError) {
          console.warn('Error fetching recent homework data:', recentError);
        }

        const completedCount =
          recentHomework?.filter((relation) => {
            if (!relation.question_attempted) return false;
            try {
              const attempted = JSON.parse(relation.question_attempted);
              return Array.isArray(attempted)
                ? attempted.length > 0
                : Object.keys(attempted).length > 0;
            } catch {
              return false;
            }
          }).length || 0;

        setStudentStats({
          totalPending,
          completedThisWeek: completedCount || 0,
          averageScore: 85, // Would calculate from actual scores
          streakDays: 3, // Would calculate from actual completion pattern
        });
      } else {
        // No pending homework
        setHomeworks([]);
        setArticles([]);
        setHomeworkStudent([]);
        setStudentStats({
          totalPending: 0,
          completedThisWeek: 0,
          averageScore: 0,
          streakDays: 0,
        });
      }
    } catch (error) {
      console.error('Error fetching homework data:', error);
      toast({
        title: 'Error loading homework',
        description:
          'Failed to load your homework assignments. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    user.user_token,
    homeworkOps,
    supabase,
    currentPage,
    pageSize,
    toast,
    currentSemester,
    semesterError,
  ]);

  // Load current semester with comprehensive error handling
  useEffect(() => {
    const loadCurrentSemester = async () => {
      try {
        console.log('🗓️ Loading current semester for Student Pending page...');
        const { data, error: fetchError } =
          await semesterOps.getCurrentSemesterYear();

        if (fetchError) {
          console.error('❌ Error fetching current semester:', fetchError);
          setSemesterError(
            'Failed to load semester information. Please try refreshing the page or contact your administrator.',
          );
          return;
        }

        if (data && data.length > 0) {
          const year = data[0].semester_year;
          console.log(`✅ Active semester found: ${year}`);
          setCurrentSemester(year);
          setSemesterError(''); // Clear any previous errors
        } else {
          console.warn('⚠️ No active semester found');
          setSemesterError(
            'No active semester is currently set. Please contact your teacher to create and activate a semester. You cannot access homework until a semester is active.',
          );
          // Don't load homework data if no active semester
          return;
        }
      } catch (err) {
        console.error('💥 Unexpected error loading current semester:', err);
        setSemesterError(
          'An unexpected error occurred while loading semester information. Please try refreshing the page or contact your administrator.',
        );
      }
    };

    if (!currentSemester) {
      loadCurrentSemester();
    }
  }, [currentSemester, semesterOps]);

  useEffect(() => {
    if (user.user_token && currentSemester && !semesterError) {
      fetchHomeworkData();
    }
  }, [fetchHomeworkData, user.user_token, currentSemester, semesterError]);

  // Combine homework with articles and add metadata (memoized for performance)
  const homeworksWithArticles = useMemo(() => {
    return homeworks.map((homework, index) => {
      // Since articles don't have ID fields, match by index
      // This assumes articles are returned in the same order as requested
      const article = articles[index];

      const difficulty = getDifficulty(homework.grade);
      const estimatedTime = getEstimatedTime(article?.article?.length || 0);

      // Use consistent fixed image for all homework tiles
      const imageSrc = getHomeworkImage(article?.article || '');

      return {
        ...homework,
        article: article ? { ...article, imageSrc } : { imageSrc },
        difficulty,
        estimatedTime,
        timeRemaining: undefined,
      } as HomeworkWithArticle;
    });
  }, [homeworks, articles]);

  // Filter and sort homeworks (optimized)
  const filteredHomeworks = useMemo(() => {
    let filtered = homeworksWithArticles;

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (hw) =>
          hw.title.toLowerCase().includes(query) ||
          (hw.grade && hw.grade.toLowerCase().includes(query)),
      );
    }

    // Difficulty filter
    if (difficultyFilter !== 'all') {
      filtered = filtered.filter((hw) => hw.difficulty === difficultyFilter);
    }

    // Sort (optimized comparisons)
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'title':
          return a.title.localeCompare(b.title);
        case 'difficulty':
          const difficultyOrder = { easy: 1, medium: 2, hard: 3 };
          return (
            (difficultyOrder[a.difficulty || 'medium'] || 2) -
            (difficultyOrder[b.difficulty || 'medium'] || 2)
          );
        case 'created_at':
          return (
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
          );
        default:
          return 0;
      }
    });

    return filtered;
  }, [homeworksWithArticles, searchQuery, difficultyFilter, sortBy]);

  // Handle homework click
  const handleHomeworkClick = (homework: HomeworkWithArticle) => {
    navigate(
      `/student/pending/${homework.homework_token}/${homework.article_token}`,
    );
  };

  // Handle preview
  const handlePreview = (homework: HomeworkWithArticle) => {
    setSelectedHomework(homework);
    onPreviewModalOpen();
  };

  // Helper functions - using fixed image for all homework tiles
  function getHomeworkImage(article: string): string {
    // find the first image src in the article
    const match = article.match(/\s*<img[^>]*>/i);
    let block = match?.[0] || `<p><img src="${book_icon}" alt="default" /></p>`;
    block = block.replace(/style="[^"]*"/gi, '');
    // Fix the image style
    block = block.replace(
      /<img/i,
      '<img style="max-width: 100%; height: 150px; display: block;"',
    );
    // console.log(block);
    return block;
  }

  function getDifficulty(grade: string | null): 'easy' | 'medium' | 'hard' {
    if (!grade) return 'easy'; // Default to easy if grade is null/undefined
    const gradeNum = parseInt(grade.split('.')[0]) || 1;
    if (gradeNum <= 2) return 'easy';
    if (gradeNum <= 4) return 'medium';
    return 'hard';
  }

  function getEstimatedTime(textLength: number): number {
    return Math.max(5, Math.ceil(textLength / 200) * 2); // Rough estimate in minutes
  }

  if (semesterError) {
    return (
      <AdminLayout>
        <VStack spacing={6} align="stretch">
          <Card>
            <VStack spacing={4} py={8}>
              <Text fontSize="lg" fontWeight="600" color="red.500">
                Unable to Load Homework
              </Text>
              <Text color="gray.600" textAlign="center">
                {semesterError}
              </Text>
            </VStack>
          </Card>
        </VStack>
      </AdminLayout>
    );
  }

  if (loading) {
    return (
      <AdminLayout>
        <VStack spacing={6} align="stretch">
          <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4}>
            {Array(4)
              .fill(0)
              .map((_, i) => (
                <Card key={i}>
                  <Skeleton height="20px" mb={4} />
                  <Skeleton height="40px" />
                </Card>
              ))}
          </SimpleGrid>
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
            {Array(6)
              .fill(0)
              .map((_, i) => (
                <Card key={i}>
                  <Skeleton height="150px" mb={4} />
                  <SkeletonText noOfLines={3} />
                </Card>
              ))}
          </SimpleGrid>
        </VStack>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <SEO
        title="Student Homework"
        description="Grand River Chinese School student homework portal. View and complete your Chinese reading comprehension assignments."
        keywords="student homework, Chinese reading, assignments, Grand River Chinese School"
      />
      <VStack spacing={6} align="stretch">
        {/* Header */}
        <HStack justify="space-between" align="center">
          <Box>
            <Text fontSize="2xl" fontWeight="bold" color="gray.800">
              {t.headerTitle}
            </Text>
            <HStack spacing={4}>
              <Text color="gray.600">
                {t.pendingAssignments(studentStats.totalPending)}
              </Text>
              {currentSemester && (
                <>
                  <Text color="gray.400">•</Text>
                  <HStack>
                    <FiCalendar size={14} />
                    <Text color="gray.600" fontSize="sm">
                      {t.semesterLabel(currentSemester)}
                    </Text>
                  </HStack>
                </>
              )}
            </HStack>
          </Box>
          <HStack spacing={2}>
            <Tooltip label={t.gridView}>
              <IconButton
                aria-label={t.gridView}
                icon={<FiGrid />}
                variant={viewMode === 'grid' ? 'solid' : 'ghost'}
                onClick={() => setViewMode('grid')}
                size="sm"
              />
            </Tooltip>
            <Tooltip label={t.listView}>
              <IconButton
                aria-label={t.listView}
                icon={<FiList />}
                variant={viewMode === 'list' ? 'solid' : 'ghost'}
                onClick={() => setViewMode('list')}
                size="sm"
              />
            </Tooltip>
          </HStack>
        </HStack>

        {/* Notification - Moved to top */}
        {/* {filteredHomeworks.length > 0 && showNotification && (
          <Alert status="info" borderRadius="md" variant="left-accent">
            <AlertIcon />
            <Box flex="1">
              <AlertDescription>
                You have {filteredHomeworks.length} unread homework
                {filteredHomeworks.length > 1 ? 's' : ''}. Complete them before
                the deadline to maintain your streak!
              </AlertDescription>
            </Box>
            <CloseButton onClick={() => setShowNotification(false)} />
          </Alert>
        )} */}

        {/* Stats Cards */}
        {/* <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4}>
          <StatsCard
            title="Pending"
            value={studentStats.totalPending.toString()}
            subtitle="assignments waiting"
            icon={FiBookOpen}
          />
          <StatsCard
            title="This Week"
            value={studentStats.completedThisWeek.toString()}
            subtitle="completed this week"
            icon={FiTrendingUp}
          />
          <StatsCard
            title="Average Score"
            value={`${studentStats.averageScore}%`}
            subtitle="your performance"
            icon={FiClock}
          />
          <StatsCard
            title="Streak"
            value={`${studentStats.streakDays}`}
            subtitle="days in a row"
            icon={FiCalendar}
          />
        </SimpleGrid> */}

        {/* Filters and Search */}
        {/* Disabled until future clarity */}
        {/* <Card>
          <VStack spacing={4}>
            <HStack justify="space-between" w="full">
              <HStack spacing={3}>
                <FiFilter />
                <Text fontWeight="medium">Filters</Text>
              </HStack>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => {
                  setSearchQuery('');
                  setDifficultyFilter('all');
                  setSortBy('created_at');
                }}
              >
                Clear All
              </Button>
            </HStack>

            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} w="full">
              <SearchInput
                placeholder="Search homework..."
                value={searchQuery}
                onChange={setSearchQuery}
              />

              <SelectInput
                placeholder="Difficulty"
                value={difficultyFilter}
                onChange={setDifficultyFilter}
                options={[
                  { value: 'all', label: 'All Difficulties' },
                  { value: 'easy', label: 'Easy' },
                  { value: 'medium', label: 'Medium' },
                  { value: 'hard', label: 'Hard' },
                ]}
              />

              <SelectInput
                placeholder="Sort by"
                value={sortBy}
                onChange={(value) => setSortBy(value as SortBy)}
                options={[
                  { value: 'created_at', label: 'Recently Added' },
                  { value: 'title', label: 'Title' },
                  { value: 'difficulty', label: 'Difficulty' },
                ]}
              />
            </SimpleGrid>
          </VStack>
        </Card> */}

        {/* Main Content */}
        <Box width="100%">
          {filteredHomeworks.length === 0 ? (
            <Card>
              <VStack spacing={4} py={12}>
                <FiBookOpen size={48} color="gray" />
                <Text fontSize="lg" color="gray.500">
                  {t.emptyTitle}
                </Text>
                <Text color="gray.400" textAlign="center">
                  {t.emptyDescription}
                </Text>
              </VStack>
            </Card>
          ) : (
            <VStack spacing={6} width="100%" align="stretch">
              <SimpleGrid
                columns={
                  viewMode === 'grid'
                    ? { base: 1, sm: 2, md: 2, lg: 3, xl: 4 }
                    : 1
                }
                spacing={6}
                width="100%"
              >
                {filteredHomeworks.map((homework) => (
                  <Card
                    key={homework.homework_token}
                    _hover={{ transform: 'translateY(-2px)', shadow: 'lg' }}
                    cursor="pointer"
                    onClick={() => handleHomeworkClick(homework)}
                    w="100%"
                    h="100%"
                    bg="white"
                    border="1px solid"
                    borderColor="gray.200"
                    boxShadow="sm"
                  >
                    <VStack spacing={4} align="stretch" h="100%">
                      {/* Image */}
                      <AspectRatio ratio={16 / 9}>
                        <Box
                          borderRadius="md"
                          overflow="hidden"
                          bg="gray.100"
                          dangerouslySetInnerHTML={{
                            __html: homework.article?.imageSrc || '',
                          }}
                          backgroundSize="cover"
                          backgroundPosition="center"
                          backgroundRepeat="no-repeat"
                        />
                      </AspectRatio>
                      {/* Content */}
                      <VStack spacing={3} align="stretch" flex={1}>
                        <HStack justify="space-between" align="start">
                          <VStack spacing={1} align="start" flex={1}>
                            <Text fontWeight="bold" fontSize="lg" noOfLines={2}>
                              {homework.title}
                            </Text>
                            <HStack spacing={2}>
                              <Badge colorScheme="blue" size="sm">
                                {t.gradeLabel(homework.grade)}
                              </Badge>
                              {/* Disabled until having a better evaluation */}
                              {/* <Badge
                                colorScheme={
                                  homework.difficulty === 'easy'
                                    ? 'green'
                                    : homework.difficulty === 'medium'
                                    ? 'yellow'
                                    : 'red'
                                }
                                size="sm"
                              >
                                {homework.difficulty?.toUpperCase()}
                              </Badge> */}
                            </HStack>
                          </VStack>
                          {/* Preview isn't working */}
                          {/* <Tooltip label="Preview">
                            <IconButton
                              aria-label="Preview homework"
                              icon={<FiInfo />}
                              size="sm"
                              variant="ghost"
                              onClick={(e) => {
                                e.stopPropagation();
                                handlePreview(homework);
                              }}
                            />
                          </Tooltip> */}
                        </HStack>

                        {/* Progress and Time */}
                        {/* <HStack
                          justify="space-between"
                          fontSize="sm"
                          color="gray.600"
                        >
                          <HStack>
                            <FiClock />
                            <Text>{t.timeMinutes(homework.estimatedTime)}</Text>
                          </HStack>
                          <Text>{homework.timeRemaining}</Text>
                        </HStack> */}
                      </VStack>

                      {/* Action Button */}
                      <Button
                        leftIcon={<FiPlay />}
                        colorScheme="blue"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleHomeworkClick(homework);
                        }}
                        size="sm"
                        mt="auto"
                      >
                        {t.startHomework}
                      </Button>
                    </VStack>
                  </Card>
                ))}
              </SimpleGrid>

              {/* Pagination */}
              {totalCount > pageSize && (
                <HStack justify="space-between" align="center" mt={6}>
                  <HStack spacing={2}>
                    <Text fontSize="sm" color="gray.600">
                      {t.showLabel}
                    </Text>
                    <Select
                      size="sm"
                      w="80px"
                      value={pageSize}
                      onChange={(e) => {
                        setPageSize(parseInt(e.target.value));
                        setCurrentPage(1);
                      }}
                    >
                      <option value={5}>5</option>
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                    </Select>
                    <Text fontSize="sm" color="gray.600">
                      {t.perPage}
                    </Text>
                  </HStack>

                  <HStack spacing={2}>
                    <Text fontSize="sm" color="gray.600">
                      {totalCount > 0 ? (
                        <>
                          {t.showingRange(
                            (currentPage - 1) * pageSize + 1,
                            Math.min(currentPage * pageSize, totalCount),
                            totalCount,
                          )}
                        </>
                      ) : (
                        t.noAssignmentsFound
                      )}
                    </Text>
                  </HStack>

                  <HStack spacing={1}>
                    <IconButton
                      aria-label={t.previousPage}
                      icon={<FiChevronLeft />}
                      size="sm"
                      variant="ghost"
                      isDisabled={currentPage === 1}
                      onClick={() => setCurrentPage((prev) => prev - 1)}
                    />

                    {(() => {
                      const totalPages = Math.ceil(totalCount / pageSize);
                      const pages = [];
                      const showPages = 3;
                      const startPage = Math.max(
                        1,
                        currentPage - Math.floor(showPages / 2),
                      );
                      const endPage = Math.min(
                        totalPages,
                        startPage + showPages - 1,
                      );

                      for (let i = startPage; i <= endPage; i++) {
                        pages.push(
                          <ChakraButton
                            key={i}
                            size="sm"
                            variant={i === currentPage ? 'solid' : 'outline'}
                            colorScheme={i === currentPage ? 'blue' : 'gray'}
                            onClick={() => setCurrentPage(i)}
                            minW="32px"
                          >
                            {i}
                          </ChakraButton>,
                        );
                      }
                      return pages;
                    })()}

                    <IconButton
                      aria-label={t.nextPage}
                      icon={<FiChevronRight />}
                      size="sm"
                      variant="ghost"
                      isDisabled={
                        currentPage >= Math.ceil(totalCount / pageSize)
                      }
                      onClick={() => setCurrentPage((prev) => prev + 1)}
                    />
                  </HStack>
                </HStack>
              )}
            </VStack>
          )}
        </Box>

        {/* Preview Modal */}
        <Modal
          isOpen={isPreviewModalOpen}
          onClose={onPreviewModalClose}
          size="4xl"
          isCentered
        >
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>
              {t.homeworkPreviewTitle(selectedHomework?.title || '')}
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody pb={6}>
              {selectedHomework && (
                <VStack spacing={6} align="stretch">
                  <HStack spacing={4}>
                    <Badge colorScheme="blue">
                      {t.gradeLabel(selectedHomework.grade)}
                    </Badge>
                    <Badge
                      colorScheme={
                        selectedHomework.difficulty === 'easy'
                          ? 'green'
                          : selectedHomework.difficulty === 'medium'
                          ? 'yellow'
                          : 'red'
                      }
                    >
                      {selectedHomework.difficulty?.toUpperCase()}
                    </Badge>
                    <Text fontSize="sm" color="gray.600">
                      {t.estimatedLabel(selectedHomework.estimatedTime)}
                    </Text>
                  </HStack>

                  <Box>
                    <Text fontWeight="bold" mb={2}>
                      {t.articlePreviewLabel}
                    </Text>
                    <Box
                      maxH="200px"
                      overflowY="auto"
                      p={4}
                      bg="gray.50"
                      borderRadius="md"
                      border="1px solid"
                      borderColor="gray.200"
                    >
                      {selectedHomework.article?.article ? (
                        <Text fontSize="sm" color="gray.700" lineHeight="1.6">
                          {(() => {
                            const cleanText = selectedHomework.article.article
                              .replace(/<[^>]*>/g, ' ') // Replace HTML tags with spaces
                              .replace(/\s+/g, ' ') // Replace multiple spaces with single space
                              .trim();

                            // Show first 800 chars or less
                            const preview = cleanText.substring(0, 800);
                            return (
                              preview + (cleanText.length > 800 ? '...' : '')
                            );
                          })()}
                        </Text>
                      ) : (
                        <Text fontSize="sm" color="gray.500" fontStyle="italic">
                          {t.noArticlePreview}
                        </Text>
                      )}
                    </Box>
                  </Box>

                  <HStack justify="flex-end" spacing={3}>
                    <Button variant="secondary" onClick={onPreviewModalClose}>
                      {t.close}
                    </Button>
                    <Button
                      leftIcon={<FiPlay />}
                      colorScheme="blue"
                      onClick={() => {
                        handleHomeworkClick(selectedHomework);
                        onPreviewModalClose();
                      }}
                    >
                      {t.startNow}
                    </Button>
                  </HStack>
                </VStack>
              )}
            </ModalBody>
          </ModalContent>
        </Modal>
      </VStack>
    </AdminLayout>
  );
};
